import React, { useState, useEffect } from 'react';
import { format, parseISO, addDays } from 'date-fns';
import { Modal } from '../ui/Modal';
import { Button } from '../ui';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function getStateAbbr(address) {
  if (!address) return null;
  const match = address.match(/,\s*([A-Z]{2})\s*\d{5}(?:-\d{4})?\s*$/)
    || address.match(/,\s*([A-Z]{2})\s*\d{0,5}\s*$/)
    || address.match(/,\s*([A-Z]{2})\s*$/)
    || address.match(/,\s*([A-Z]{2})\s*,/)
    || address.match(/\b([A-Z]{2})\s+\d{5}/);
  return match ? match[1] : null;
}

function buildWeeklySchedule(crewMember, weekDates, jobs, availability, crew, clients, trucks) {
  const weekStart = parseISO(weekDates[0]);
  let msg = `BEATY MOVING — Schedule for Week of ${format(weekStart, 'M/d')}\n\n`;

  const outOfTownNotes = [];

  weekDates.forEach((dateStr, idx) => {
    const dayName = DAY_NAMES[parseISO(dateStr).getDay()];
    const dayJobs = jobs.filter(j => j.move_date === dateStr && (j.crew_ids || []).includes(crewMember.id)).sort((a, b) => (a.day_sequence || 1) - (b.day_sequence || 1));
    const avail = availability.find(a => a.crew_id === crewMember.id && a.date === dateStr);

    if (dayJobs.length > 0) {
      const time = dayJobs[0].time || '8:00';
      const states = dayJobs.map(j => getStateAbbr(j.to_address)).filter(s => s && s !== 'NC');
      const stateTag = states.length > 0 ? ` (${states[0]})` : '';
      if (states.length > 0) {
        const companions = [...new Set(dayJobs.flatMap(j => j.crew_ids || []))].filter(id => id !== crewMember.id).map(id => crew.find(c => c.id === id)?.name).filter(Boolean);
        outOfTownNotes.push({ dayName, state: states[0], companions });
      }
      const onCallTag = avail?.status === 'on_call' ? ' (On Call)' : '';
      msg += `${dayName} ${time}${stateTag}${onCallTag}\n`;
    } else if (avail) {
      if (avail.status === 'off_requested') {
        msg += `${dayName} OFF\n`;
      } else if (avail.status === 'scheduled_off') {
        msg += `${dayName} Scheduled Off\n`;
      } else if (avail.status === 'on_call') {
        msg += `${dayName} On Call\n`;
      } else if (avail.status === 'office') {
        msg += `${dayName} Office\n`;
      }
    } else {
      msg += `${dayName} —\n`;
    }
  });

  if (outOfTownNotes.length > 0) {
    const note = outOfTownNotes[0];
    const dayList = outOfTownNotes.map(n => n.dayName);
    const companions = [...new Set(outOfTownNotes.flatMap(n => n.companions))];
    msg += `\nYou'll be going to ${note.state}`;
    if (dayList.length === 1) msg += ` on ${dayList[0]}`;
    else msg += ` ${dayList[0]}–${dayList[dayList.length - 1]}`;
    if (companions.length) msg += ` with ${companions.join(', ')}`;
    msg += `.\n`;
  }

  msg += `\nPls confirm you've received. Thank you!`;
  return msg;
}

export default function WeeklyScheduleModal({ crew, weekDates, weekStart, jobs, availability, clients, trucks, onClose, onSent, onError }) {
  const [selectedPrev, setSelectedPrev] = useState(null);
  const [sending, setSending] = useState(false);

  const activeCrew = crew.filter(m => m.active !== false);
  const previews = activeCrew.map(member => {
    const msg = buildWeeklySchedule(member, weekDates, jobs, availability, crew, clients, trucks);
    const hasJobs = weekDates.some(d => jobs.some(j => j.move_date === d && (j.crew_ids || []).includes(member.id)));
    const hasAvail = weekDates.some(d => availability.some(a => a.crew_id === member.id && a.date === d));
    return { member, msg, hasPhone: !!member.phone, hasActivity: hasJobs || hasAvail };
  }).filter(p => p.hasActivity);

  const sendable = previews.filter(p => p.hasPhone);
  const noPhone = previews.filter(p => !p.hasPhone);

  const handleSendAll = async () => {
    setSending(true);
    try {
      const messages = sendable.map(p => ({ to: p.member.phone, body: p.msg }));
      const response = await fetch('/api/dispatch-sms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed');
      onSent(`Weekly schedule sent to ${data.sent} crew member${data.sent !== 1 ? 's' : ''}${data.failed ? ` (${data.failed} failed)` : ''}`);
    } catch (e) { onError('Error: ' + e.message); }
    finally { setSending(false); }
  };

  return (
    <Modal title={`Weekly Schedule — Week of ${format(weekStart, 'MMM d')}`} onClose={onClose} width="max-w-3xl">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">Preview and send the weekly schedule SMS to all active crew members. Only crew with jobs or availability set for this week are shown.</p>

        {previews.length === 0 && (
          <div className="text-center py-8 text-slate-400">No crew members have jobs or availability set for this week.</div>
        )}

        <div className="max-h-96 overflow-y-auto space-y-2">
          {previews.map(p => (
            <div key={p.member.id} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedPrev(selectedPrev === p.member.id ? null : p.member.id)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.member.display_color }}></div>
                  <span className="font-medium text-sm">{p.member.name}</span>
                  {p.hasPhone ? <span className="text-xs text-green-600">✓ {p.member.phone}</span> : <span className="text-xs text-red-500">No phone</span>}
                </div>
                <span className="text-xs text-slate-400">{selectedPrev === p.member.id ? '▲ Hide' : '▼ Preview'}</span>
              </div>
              {selectedPrev === p.member.id && (
                <pre className="mt-2 p-3 bg-slate-900 text-green-400 text-xs rounded-lg whitespace-pre-wrap font-mono overflow-auto max-h-48">{p.msg}</pre>
              )}
            </div>
          ))}
        </div>

        {noPhone.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-xs text-yellow-800">
            {noPhone.length} crew member{noPhone.length > 1 ? 's' : ''} without phone: {noPhone.map(p => p.member.name).join(', ')}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={sending}
            disabled={sendable.length === 0}
            onClick={handleSendAll}
          >
            📱 Send to {sendable.length} Crew
          </Button>
        </div>
      </div>
    </Modal>
  );
}
